import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { PaymentDeviceAccessory } from '@/models/common/PaymentDeviceEnums';
import { PaymentDeviceRentalDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { HandOverPaymentDeviceDtoOut, ReturnPaymentDeviceDtoOut } from '@/models/out/PaymentDeviceDtoOut';
import { ALL_ACCESSORIES, sortAccessories } from '@/helpers/paymentDeviceAccessories';
import { MANAGEMENT_DEVICES_URL, dateOnly, paymentDeviceErrorMessage, todayInIsrael } from '@/helpers/paymentDevices';
import usePaymentDeviceApi from '../usePaymentDeviceApi';
import AccessoriesField from './AccessoriesField';
import PendingRequestLinkField from './PendingRequestLinkField';
import { DateField, DialogActions, ErrorLine, NoteField, RadioGroup } from './dialogParts';

/** The status actions that need a dialog - everything except edit, assign and delete. */
export type DeviceStatusAction = 'handOver' | 'cancelAssignment' | 'return' | 'editHandOverDate' | 'markFaulty' | 'markFixed' | 'retire';

/** What an action acts on: a device, and its open rental for the rental actions. */
export interface DeviceActionTarget {
  deviceId: number;
  deviceNumber: string;
  rental: PaymentDeviceRentalDtoIn | null;
}

interface ActionSpec {
  titleKey: string;
  confirmKey: string;
  /** The rental actions are addressed by rental id, the others by device id. */
  url: (target: DeviceActionTarget) => string;
  hasDate: boolean;
  noteLabelKey: string;
  noteRequired: boolean;
  hintKey?: string;
}

const rentalUrl = (target: DeviceActionTarget, verb: string) => `${MANAGEMENT_DEVICES_URL}/rentals/${target.rental?.id}/${verb}`;
const deviceUrl = (target: DeviceActionTarget, verb: string) => `${MANAGEMENT_DEVICES_URL}/${target.deviceId}/${verb}`;

const SPECS: Record<DeviceStatusAction, ActionSpec> = {
  handOver: {
    titleKey: 'paymentDevices.dialogs.handOverTitle',
    confirmKey: 'paymentDevices.actions.handOver',
    url: (target) => rentalUrl(target, 'hand-over'),
    hasDate: true,
    noteLabelKey: 'paymentDevices.dialogs.notesOptional',
    noteRequired: false,
  },
  cancelAssignment: {
    titleKey: 'paymentDevices.dialogs.cancelTitle',
    confirmKey: 'paymentDevices.actions.cancelAssignment',
    url: (target) => rentalUrl(target, 'cancel'),
    hasDate: false,
    noteLabelKey: 'paymentDevices.dialogs.reason',
    noteRequired: true,
    hintKey: 'paymentDevices.dialogs.cancelHint',
  },
  return: {
    titleKey: 'paymentDevices.dialogs.returnTitle',
    confirmKey: 'paymentDevices.actions.return',
    url: (target) => rentalUrl(target, 'return'),
    hasDate: true,
    noteLabelKey: 'paymentDevices.dialogs.notesOptional',
    noteRequired: false,
  },
  editHandOverDate: {
    titleKey: 'paymentDevices.dialogs.editHandOverDateTitle',
    confirmKey: 'paymentDevices.dialogs.save',
    url: (target) => rentalUrl(target, 'hand-over-date'),
    hasDate: true,
    noteLabelKey: 'paymentDevices.dialogs.editHandOverDateReason',
    noteRequired: true,
    hintKey: 'paymentDevices.dialogs.editHandOverDateHint',
  },
  markFaulty: {
    titleKey: 'paymentDevices.dialogs.markFaultyTitle',
    confirmKey: 'paymentDevices.actions.markFaulty',
    url: (target) => deviceUrl(target, 'mark-faulty'),
    hasDate: false,
    noteLabelKey: 'paymentDevices.dialogs.markFaultyNote',
    noteRequired: true,
  },
  markFixed: {
    titleKey: 'paymentDevices.dialogs.markFixedTitle',
    confirmKey: 'paymentDevices.actions.markFixed',
    url: (target) => deviceUrl(target, 'mark-fixed'),
    hasDate: false,
    noteLabelKey: 'paymentDevices.dialogs.notesOptional',
    noteRequired: false,
  },
  retire: {
    titleKey: 'paymentDevices.dialogs.retireTitle',
    confirmKey: 'paymentDevices.actions.retire',
    url: (target) => deviceUrl(target, 'retire'),
    hasDate: false,
    noteLabelKey: 'paymentDevices.dialogs.reason',
    noteRequired: true,
  },
};

interface DeviceActionDialogProps {
  action: DeviceStatusAction | null;
  target: DeviceActionTarget | null;
  onClose: () => void;
}

/** One dialog for the seven status actions: a date where the action has one, a condition on return, and a note. */
const DeviceActionDialog = ({ action, target, onClose }: DeviceActionDialogProps) => {
  const { t } = useTranslation();
  const api = usePaymentDeviceApi();
  const today = todayInIsrael();
  const open = action != null && target != null;

  const [date, setDate] = useState(today);
  const [faulty, setFaulty] = useState<'ok' | 'faulty'>('ok');
  const [note, setNote] = useState('');
  const [requestId, setRequestId] = useState<number | null>(null);
  const [accessories, setAccessories] = useState<PaymentDeviceAccessory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // A correction starts from the date on record.
    setDate(action === 'editHandOverDate' ? dateOnly(target?.rental?.handedOverDate) || today : today);
    setFaulty('ok');
    setNote('');
    // Most hand-overs include everything, and most returns bring back all that went out.
    setAccessories(action === 'return' ? sortAccessories(target?.rental?.handedOverAccessories) : ALL_ACCESSORIES);
    // requestId is left to PendingRequestLinkField, which picks the default itself (see AssignDeviceDialog).
    setError(null);
  }, [open, action, target, today]);

  if (!open) return null;
  const spec = SPECS[action];
  const handedOverDate = dateOnly(target.rental?.handedOverDate);
  // A hand-over of a manual assignment can still answer the fund's pending request.
  const offersRequestLink = action === 'handOver' && target.rental != null && target.rental.requestId == null;
  // At return only what went out can be checked back in; a rental from before accessories has none.
  const handedOverAccessories = sortAccessories(target.rental?.handedOverAccessories);

  const handleSubmit = async () => {
    if (spec.hasDate && !date) {
      setError(t('paymentDevices.dialogs.datesRequired'));
      return;
    }
    if (spec.hasDate && date > today) {
      setError(t('paymentDevices.dialogs.dateNotInFuture'));
      return;
    }
    if (action === 'return' && handedOverDate && date < handedOverDate) {
      setError(t('paymentDevices.dialogs.returnBeforeHandOver'));
      return;
    }
    if (spec.noteRequired && !note.trim()) {
      setError(t(action === 'markFaulty' ? 'paymentDevices.dialogs.noteRequired' : 'paymentDevices.dialogs.reasonRequired'));
      return;
    }

    const trimmed = note.trim() || null;
    const body: object = action === 'return'
      ? { date, faulty: faulty === 'faulty', note: trimmed, returnedAccessories: accessories } satisfies ReturnPaymentDeviceDtoOut
      : action === 'handOver'
        ? { date, note: trimmed, requestId: offersRequestLink ? requestId : null, accessories } satisfies HandOverPaymentDeviceDtoOut
        : spec.hasDate ? { date, note: trimmed } : { note: trimmed };

    setError(null);
    setIsSubmitting(true);
    try {
      await api.post<unknown, object>(spec.url(target), body);
      onClose();
    } catch (err) {
      setError(paymentDeviceErrorMessage(err, t, 'paymentDevices.errors.save'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainDialogLayout
      open
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}
      title={t(spec.titleKey, { number: target.deviceNumber, fundId: target.rental?.fundId ?? '' })}
      maxWidth="520px"
      fitContent
    >
      <div className="flex flex-col gap-4 p-4">
        {spec.hintKey && <p className="body-sm-regular text-(--font-secondary)">{t(spec.hintKey)}</p>}
        {spec.hasDate && (
          <DateField
            id="pd-action-date"
            label={t(action === 'return' ? 'paymentDevices.dialogs.returnDate' : 'paymentDevices.dialogs.handOverDate')}
            value={date}
            onChange={setDate}
            min={action === 'return' ? handedOverDate || undefined : undefined}
            max={today}
          />
        )}
        {action === 'return' && (
          <RadioGroup
            name="pd-action-condition"
            label={t('paymentDevices.dialogs.condition')}
            value={faulty}
            onChange={setFaulty}
            options={[
              { value: 'ok', label: t('paymentDevices.dialogs.conditionOk') },
              { value: 'faulty', label: t('paymentDevices.dialogs.conditionFaulty') },
            ]}
          />
        )}
        {action === 'handOver' && (
          <AccessoriesField
            label={t('paymentDevices.accessories.handedOverLabel')}
            options={ALL_ACCESSORIES}
            value={accessories}
            onChange={setAccessories}
          />
        )}
        {action === 'return' && handedOverAccessories.length > 0 && (
          <AccessoriesField
            label={t('paymentDevices.accessories.returnedLabel')}
            options={handedOverAccessories}
            value={accessories}
            onChange={setAccessories}
            warnMissing
          />
        )}
        {offersRequestLink && (
          <PendingRequestLinkField fundId={target.rental?.fundId ?? null} value={requestId} onChange={setRequestId} />
        )}
        <NoteField id="pd-action-note" label={t(spec.noteLabelKey)} value={note} onChange={setNote} />
        <ErrorLine error={error} />
        <DialogActions onCancel={onClose} onConfirm={handleSubmit} isSubmitting={isSubmitting} confirmText={t(spec.confirmKey)} />
      </div>
    </MainDialogLayout>
  );
};

export default DeviceActionDialog;
